import {DateTime} from "luxon";
import {getFullstackUrl} from "@/composables/useFullstackUrl";

const FULLSTACK = getFullstackUrl();

export function getNewsDate(date) {
  return DateTime.fromISO(date).toFormat("d MMMM yyyy");
}

// [{key: "2026-04", label: "April 2026", items: [...]}], newest first
export function getNewsMonths(items) {
  const months = [];
  for (const item of items) {
    const key = item.date.slice(0, 7);
    if (months.at(-1)?.key !== key) {
      months.push({key, label: DateTime.fromISO(key).toFormat("MMMM yyyy"), items: []});
    }
    months.at(-1).items.push(item);
  }
  return months;
}

// "30 April" (the year is in the breadcrumb and the year's page)
export function getNewsDay(date) {
  return DateTime.fromISO(date).toFormat("d MMMM");
}

// "April 30", for the item page's breadcrumb (News / 2026 / April 30)
export function getNewsMonthDay(date) {
  return DateTime.fromISO(date).toFormat("MMMM d");
}

// items on one date, in a fixed order (by slug): the first is "04-30", the next "04-30-b"…
function getSameDateSlugs(date, items) {
  return items.filter((item) => item.date === date).map((item) => item.slug).sort();
}

// an item's page: /news/2026/04-30, or /news/2026/04-30-b when it's the second on that date
export function getNewsItemPath(item, items) {
  const index = getSameDateSlugs(item.date, items).indexOf(item.slug);
  const suffix = index > 0 ? `-${String.fromCharCode(97 + index)}` : "";
  return `/news/${item.date.slice(0, 4)}/${item.date.slice(5)}${suffix}`;
}

// the inverse: year "2026" + day "04-30-b" → that item (or undefined)
export function findNewsItem(year, day, items) {
  const [, monthDay, letter] = String(day).match(/^(\d{2}-\d{2})(?:-([a-z]))?$/) || [];
  if (!monthDay) return undefined;
  const slugs = getSameDateSlugs(`${year}-${monthDay}`, items);
  const slug = slugs[letter ? letter.charCodeAt(0) - 97 : 0];
  return items.find((item) => item.slug === slug);
}

// /news redirects to the current year's page, /news/2026; earlier years are /news/2025 etc.
export function getCurrentNewsYear() {
  return String(new Date().getFullYear());
}

async function fetchJson(path, fallback) {
  try {
    const response = await fetch(`${FULLSTACK}${path}`);
    return response.ok ? await response.json() : fallback;
  } catch (error) {
    console.error(`useNews: ${error}`);
    return fallback;
  }
}

const yearRequests = new Map(); // year → pending/settled list, shared across components

// one year's items, newest first: [{slug, date, title, excerpt, source}]
export function fetchNewsYear(year) {
  if (!yearRequests.has(year)) {
    const request = fetchJson(`/api/v1/news?year=${year}`, null);
    yearRequests.set(year, request.then((items) => {
      if (!items) yearRequests.delete(year); // retry on the next visit
      return items || [];
    }));
  }
  return yearRequests.get(year);
}

let archiveRequest = null;

// [{year, months: [{key: "2026-04", count}]}], newest first
export function fetchNewsArchive() {
  if (!archiveRequest) {
    archiveRequest = fetchJson("/api/v1/news/archive", null).then((archive) => {
      if (!archive) archiveRequest = null;
      return archive || [];
    });
  }
  return archiveRequest;
}

const itemCache = new Map(); // slug → message, shared by the list (expanded rows) and item page

async function fetchNewsItemFromServer(slug) {
  try {
    const response = await fetch(`${FULLSTACK}/api/v1/news/${encodeURIComponent(slug)}`);
    return response.ok ? await response.json() : null;
  } catch (error) {
    console.error(`useNews: ${error}`);
    return null;
  }
}

export async function fetchNewsItem(slug) {
  if (itemCache.has(slug)) return itemCache.get(slug);
  const item = await fetchNewsItemFromServer(slug);
  if (item) itemCache.set(slug, item);
  return item;
}

const VIZBI_HOST = /^(www\.)?vizbi\.org$/i;

// links inside an email point at https://vizbi.org/...: navigate those within the app
export function followInternalLink(event, router) {
  const link = event.target.closest("a[href]");
  if (!link) return;
  const url = new URL(link.href);
  if (!VIZBI_HOST.test(url.hostname)) return;
  event.preventDefault();
  router.push(`${url.pathname}${url.search}${url.hash}`);
}
